/**
 * PWA: Service Worker (/sw.js) registrieren, Web-Push (VAPID) an-/abmelden, App-Badge pflegen.
 * Push-Endpunkte laut Vertrag 4.5; der VAPID-Schlüssel kommt aus GET /auth/config (Fallback /push/vapid-public-key).
 */
import type { PushSubscriptionInput } from '@lagerfeuer/shared';
import { authApi, pushApi } from './endpoints';

export type PushState = 'unsupported' | 'unavailable' | 'denied' | 'enabled' | 'disabled';

/** Base64url (VAPID-Schlüssel) → Uint8Array für `applicationServerKey`. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.trim() + '='.repeat((4 - (base64.trim().length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Browser-Abo → Body für POST /push/subscriptions. */
export function subscriptionToInput(sub: Pick<PushSubscription, 'toJSON'>): PushSubscriptionInput {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error('Unvollständiges Push-Abo');
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }, expirationTime: json.expirationTime ?? null };
}

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Service Worker nur in Produktions-Builds (im Dev-Server stören Caches) oder mit `localStorage.lfSw = '1'`. */
function swWanted(): boolean {
  if (!('serviceWorker' in navigator)) return false;
  try {
    if (localStorage.getItem('lfSw') === '1') return true;
  } catch {
    // Speicher gesperrt – ignorieren.
  }
  return import.meta.env.PROD;
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

/** Registriert /sw.js (einmalig) und verdrahtet Navigation aus Benachrichtigungs-Klicks. */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return registration;
  if (!swWanted()) return (registration = Promise.resolve(null));
  navigator.serviceWorker.addEventListener('message', onSwMessage);
  registration = new Promise((resolve) => {
    const go = () => {
      navigator.serviceWorker.register('/sw.js', { scope: '/' }).then(resolve, () => resolve(null));
    };
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  });
  return registration;
}

/** Nachricht vom Service Worker: `{ type: 'navigate', url }` nach Klick auf eine Benachrichtigung. */
function onSwMessage(e: MessageEvent): void {
  const data = e.data as { type?: string; url?: string } | null;
  if (data?.type !== 'navigate' || typeof data.url !== 'string') return;
  const url = new URL(data.url, window.location.origin);
  if (url.origin !== window.location.origin) return;
  window.history.pushState({}, '', url.pathname + url.search + url.hash);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

async function swRegistration(timeoutMs = 8000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration('/').catch(() => undefined);
  if (existing) return existing;
  // Noch nicht registriert (z. B. Dev-Server): jetzt registrieren.
  await navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => null);
  return Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), timeoutMs))]);
}

export async function vapidPublicKey(): Promise<string | null> {
  const cfg = await authApi.config().catch(() => null);
  if (cfg?.vapidPublicKey) return cfg.vapidPublicKey;
  const res = await pushApi.vapidKey().catch(() => null);
  return res?.key ?? null;
}

/** Aktueller Push-Zustand dieses Browsers. */
export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration('/').catch(() => undefined);
  const sub = await reg?.pushManager.getSubscription().catch(() => null);
  return sub ? 'enabled' : 'disabled';
}

/** Berechtigung anfragen, Abo anlegen und beim Server hinterlegen. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const key = await vapidPublicKey();
  if (!key) return 'unavailable';
  const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'disabled';
  const reg = await swRegistration();
  if (!reg) throw new Error('Service Worker nicht verfügbar');
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, key)) {
    await sub.unsubscribe().catch(() => false);
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
  await pushApi.subscribe(subscriptionToInput(sub));
  return 'enabled';
}

function sameKey(sub: PushSubscription, key: string): boolean {
  const current = sub.options?.applicationServerKey;
  if (!current) return true;
  const a = new Uint8Array(current);
  const b = urlBase64ToUint8Array(key);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Abo im Browser und beim Server entfernen. */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const reg = await navigator.serviceWorker.getRegistration('/').catch(() => undefined);
  const sub = await reg?.pushManager.getSubscription().catch(() => null);
  if (sub) {
    await pushApi.unsubscribe(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => false);
  }
  return 'disabled';
}

/**
 * Vorhandenes Browser-Abo erneut beim Server hinterlegen (Upsert über `endpoint`), z. B. nach dem Anmelden
 * oder wenn der Browser das Abo erneuert hat. Fragt nie nach Berechtigungen.
 */
export async function resyncPush(): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration('/').catch(() => undefined);
  const sub = await reg?.pushManager.getSubscription().catch(() => null);
  if (!sub) return false;
  await pushApi.subscribe(subscriptionToInput(sub));
  return true;
}

/** Ungelesen-Zähler als App-Badge (installierte PWA, sofern unterstützt). */
export function setAppBadge(count: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (count > 0) void nav.setAppBadge?.(count)?.catch(() => undefined);
    else void nav.clearAppBadge?.()?.catch(() => undefined);
  } catch {
    // Nicht unterstützt.
  }
}
